import { useEffect, useState, useCallback } from "react";

const KEY = "ph_theme";
const EVENT = "ph-theme-change";

function readInitial() {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {}
  // First-time visitors get system preference.
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

function apply(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

function currentTheme() {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "light" || attr === "dark") return attr;
  }
  return readInitial();
}

// Single source of truth: apply to <html>, persist, and notify every consumer.
function setThemeGlobal(theme) {
  apply(theme);
  try { localStorage.setItem(KEY, theme); } catch {}
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: theme }));
  }
}

/**
 * Players Health dark/light theme switcher.
 * Sets [data-theme] on <html>, persists choice in localStorage, follows system
 * preference for first-time visitors, and broadcasts changes so ALL mounted
 * consumers (Logo, Sidebar, Login, toasts…) stay in sync — not just the toggler.
 */
export function useTheme() {
  const [theme, setThemeState] = useState(currentTheme);

  useEffect(() => {
    const onChange = (e) => {
      const next = e.detail;
      setThemeState((prev) => (prev === next ? prev : next));
    };
    window.addEventListener(EVENT, onChange);
    // Re-sync in case a change happened between initial read and subscription.
    setThemeState((prev) => (prev === currentTheme() ? prev : currentTheme()));
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  const setTheme = useCallback((next) => {
    const value = typeof next === "function" ? next(currentTheme()) : next;
    setThemeGlobal(value);
  }, []);

  const toggle = useCallback(() => {
    setThemeGlobal(currentTheme() === "light" ? "dark" : "light");
  }, []);

  return { theme, setTheme, toggle };
}

// Eager: apply stored theme as soon as this module is imported,
// before React renders, so the first paint matches the user's choice.
apply(readInitial());
